import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen,
  Plus,
  Search,
  Edit2,
  Trash2,
  Layers,
  Users,
  ShieldCheck,
  Clock,
  Loader2,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  UploadCloud,
  FileText,
  X,
  CheckCircle2,
} from "lucide-react";
import { adminMagazineAPI } from "./magazine.api";
import {
  useAdminMagazines,
  useAdminMagazineRoles,
  useDeleteMagazineMutation,
} from "./useMagazineQueries";
import { toast } from "react-toastify";

const resolveCoverUrl = (urlOrKey) => {
  if (!urlOrKey) return "";
  if (
    urlOrKey.startsWith("http://") ||
    urlOrKey.startsWith("https://") ||
    urlOrKey.startsWith("blob:") ||
    urlOrKey.startsWith("data:")
  ) {
    return urlOrKey;
  }
  const cleanKey = urlOrKey.replace(/^\/+/, "");
  return `https://d23ksuhb21eg5b.cloudfront.net/${cleanKey}`;
};

export default function AdminMagazines() {
  const [search, setSearch] = useState("");
  const [accessFilter, setAccessFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const limit = 10;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("import") === "true") {
      setImportModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Queries & Mutations
  const { data: rolesData } = useAdminMagazineRoles();
  const systemRoles = Array.isArray(rolesData) ? rolesData : [];

  const { data, isLoading, isFetching } = useAdminMagazines({
    page,
    limit,
    access_type: accessFilter !== "ALL" ? accessFilter : undefined,
    role: roleFilter !== "ALL" ? roleFilter : undefined,
    search: search.trim() || undefined,
  });

  const deleteMagazineMutation = useDeleteMagazineMutation();

  const rawMagazines = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
    ? data
    : [];

  // Client-side fallback filter
  const magazines = rawMagazines.filter((mag) => {
    const matchesSearch =
      !search ||
      mag.title?.toLowerCase().includes(search.toLowerCase()) ||
      mag.slug?.toLowerCase().includes(search.toLowerCase());
    const matchesAccess =
      accessFilter === "ALL" || mag.access_type === accessFilter;
    const matchesRole =
      roleFilter === "ALL" ||
      (mag.allowed_roles && mag.allowed_roles.some((r) => r.toLowerCase() === roleFilter.toLowerCase())) ||
      (mag.allowed_groups && mag.allowed_groups.toLowerCase().includes(roleFilter.toLowerCase()));
    return matchesSearch && matchesAccess && matchesRole;
  });

  const totalItems = data?.total || rawMagazines.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;

  const handleDeleteEdition = async (id, title) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${title}"? All uploaded pages and leads will be permanently deleted.`
      )
    ) {
      return;
    }
    try {
      await deleteMagazineMutation.mutateAsync(id);
      toast.success("Publication deleted successfully.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete publication.");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto bg-slate-50/50 min-h-screen">
      {/* ── TOP ACTION HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
        <div>
          <div className="flex items-center gap-3 mb-1.5">
            <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600">
              <BookOpen className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">
              Publications &amp; Magazines
            </h1>
          </div>
          <p className="text-xs text-slate-500 ml-0.5">
            Create editions, manage pages via direct S3 upload, configure DRM &amp; access controls, and export reader leads.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <button
            onClick={() => setImportModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            <UploadCloud className="w-4 h-4 text-orange-400" />
            <span>Import PDF</span>
          </button>

          <button
            onClick={() => navigate("/admin/magazines/create")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold shadow-sm shadow-orange-600/20 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>New Edition</span>
          </button>
        </div>
      </div>

      {/* ── SEARCH & FILTER CONTROLS ── */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            placeholder="Search by title or slug..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-3.5 py-2 text-xs rounded-xl bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Role Filter Selector */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-2xs">
            <span className="text-[11px] text-slate-500 font-medium">Role:</span>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="bg-transparent text-xs text-slate-800 font-semibold focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Roles</option>
              {systemRoles.map((r) => {
                const roleName = r.name || r.role_name;
                return (
                  <option key={r.id || r.role_id || roleName} value={roleName}>
                    {roleName}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Access Type Filter Badges */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {["ALL", "FREE", "EMAIL_LEAD", "PASSCODE", "PAID"].map((t) => (
              <button
                key={t}
                onClick={() => {
                  setAccessFilter(t);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border cursor-pointer ${
                  accessFilter === t
                    ? "bg-orange-600 text-white border-orange-600 shadow-xs"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {t === "ALL" ? "All Types" : t.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── EDITIONS TABLE ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-16 flex flex-col items-center justify-center text-slate-400 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <span className="text-xs font-medium text-slate-500">Loading editions...</span>
          </div>
        ) : magazines.length === 0 ? (
          <div className="p-16 text-center text-slate-500 bg-white">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-3.5 text-slate-400">
              <BookOpen className="w-8 h-8" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">
              No publications found
            </h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mb-5 leading-relaxed">
              {search || accessFilter !== "ALL" || roleFilter !== "ALL"
                ? "No editions matched your search or filter criteria. Try clearing filters."
                : "Get started by creating your first magazine publication edition."}
            </p>
            <button
              onClick={() => navigate("/admin/magazines/create")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold shadow-sm transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Create Edition</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-semibold">
                  <th className="py-3.5 px-4">Edition</th>
                  <th className="py-3.5 px-3">Access &amp; Roles</th>
                  <th className="py-3.5 px-3">DRM / Security</th>
                  <th className="py-3.5 px-3">Pages</th>
                  <th className="py-3.5 px-3">Leads</th>
                  <th className="py-3.5 px-3">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {magazines.map((mag) => (
                  <tr
                    key={mag.id}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    {/* Cover + Title */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-14 rounded-lg bg-slate-50 overflow-hidden shrink-0 border border-slate-200 flex items-center justify-center text-slate-400">
                          {mag.cover_image ? (
                            <img
                              src={resolveCoverUrl(mag.cover_image)}
                              alt={mag.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <BookOpen className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 leading-tight">
                            {mag.title}
                          </div>
                          <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                            /{mag.slug}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Access Type Badge & Role-wise Chips */}
                    <td className="py-3.5 px-3">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                          {mag.access_type ? mag.access_type.replace("_", " ") : "FREE"}
                        </span>
                        {mag.access_type === "MEMBER_ONLY" && (
                          <div className="flex flex-wrap gap-1 max-w-[200px] mt-0.5">
                            {Array.isArray(mag.allowed_roles) && mag.allowed_roles.length > 0 ? (
                              mag.allowed_roles.map((r) => (
                                <span
                                  key={r}
                                  className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                                >
                                  {r}
                                </span>
                              ))
                            ) : mag.allowed_groups ? (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                {mag.allowed_groups}
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">No roles set</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* DRM Status */}
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-2">
                        {mag.allow_screenshot ? (
                          <span className="text-slate-500 text-[11px]" title="Screenshots Allowed">
                            Open DRM
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"
                            title="Watermark + Shield Active"
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            <span>Shielded</span>
                          </span>
                        )}
                        {mag.access_duration_days > 0 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 font-medium border border-amber-200">
                            {mag.access_duration_days}d rental
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Pages — navigates to Dashboard */}
                    <td className="py-3.5 px-3">
                      <button
                        onClick={() =>
                          navigate(`/admin/magazines/${mag.id}/manage?tab=pages`)
                        }
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-[11px] transition-colors cursor-pointer border border-slate-200"
                      >
                        <Layers className="w-3 h-3 text-orange-500" />
                        <span>{mag.page_count ?? (mag.pages?.length || 0)} Pages</span>
                      </button>
                    </td>

                    {/* Leads — navigates to Dashboard */}
                    <td className="py-3.5 px-3">
                      <button
                        onClick={() =>
                          navigate(`/admin/magazines/${mag.id}/manage?tab=leads`)
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                      >
                        <Users className="w-3 h-3" />
                        <span>{mag.lead_count ?? 0} Leads</span>
                      </button>
                    </td>

                    {/* Published */}
                    <td className="py-3.5 px-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                          mag.is_published
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-600 border-slate-200"
                        }`}
                      >
                        {mag.is_published ? "Published" : "Draft"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Live reader preview */}
                        <a
                          href={`/magazine/${mag.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                          title="View Live Reader"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>

                        {/* Requests — navigates to Dashboard */}
                        {mag.require_admin_approval && (
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/magazines/${mag.id}/manage?tab=requests`
                              )
                            }
                            className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors cursor-pointer"
                            title="Access Requests"
                          >
                            <Clock className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {/* Edit */}
                        <button
                          onClick={() =>
                            navigate(`/admin/magazines/edit/${mag.id}`)
                          }
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-orange-600 transition-colors cursor-pointer"
                          title="Edit Edition"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteEdition(mag.id, mag.title)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                          title="Delete Edition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── PAGINATION CONTROLS ── */}
        {totalItems > limit && (
          <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-between text-xs text-slate-600">
            <span>
              Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalItems)} of {totalItems} editions
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 bg-white text-slate-700 disabled:opacity-40 cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 font-medium">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 bg-white text-slate-700 disabled:opacity-40 cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── IMPORT PDF MODAL ── */}
      <ImportPdfModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        systemRoles={systemRoles}
        onSuccess={(newMag) => {
          if (newMag?.id) {
            navigate(`/admin/magazines/${newMag.id}/manage`);
          }
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPORT PDF MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ImportPdfModal({ isOpen, onClose, systemRoles = [], onSuccess }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [accessType, setAccessType] = useState("FREE");
  const [allowedGroups, setAllowedGroups] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const baseName = f.name
      .replace(/\.[^/.]+$/, "")
      .replace(/[_\-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
    setTitle(baseName);
    setSlug(
      baseName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
    );
  };

  const handleToggleRole = (roleName) => {
    let current = allowedGroups.split(",").map((s) => s.trim()).filter(Boolean);
    if (current.includes(roleName)) {
      current = current.filter((r) => r !== roleName);
    } else {
      current.push(roleName);
    }
    setAllowedGroups(current.join(", "));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a PDF file.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatusMessage("Uploading PDF to server...");

    try {
      const formData = new FormData();
      formData.append("pdf_file", file);
      formData.append("title", title || file.name);
      formData.append("slug", slug || "magazine");
      if (description) formData.append("description", description);
      formData.append("access_type", accessType);
      if (allowedGroups) formData.append("allowed_groups", allowedGroups);
      formData.append("preview_pages_count", 0);
      formData.append("quality", "98");
      formData.append("scale", "4.5");
      formData.append("overwrite", "true");

      const result = await adminMagazineAPI.importPdf(formData, (percent) => {
        setProgress(percent);
        if (percent < 100) {
          setStatusMessage(`Uploading PDF (${percent}%)...`);
        } else {
          setStatusMessage("Converting PDF pages to Retina WebP & uploading to S3...");
        }
      });

      toast.success(`Magazine "${result.title}" imported successfully!`);
      onClose();
      onSuccess(result);
    } catch (err) {
      console.error("PDF import error:", err);
      toast.error(
        err.response?.data?.detail || err.message || "Failed to import PDF."
      );
    } finally {
      setUploading(false);
      setProgress(0);
      setStatusMessage("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <UploadCloud className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">Import Magazine from PDF</h2>
              <p className="text-[11px] text-slate-500">
                Converts ANY PDF into a high-res flipbook with WebP pages and thumbnails.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4">
          {/* File Selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Select PDF File <span className="text-red-500">*</span>
            </label>
            <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 hover:border-orange-500 rounded-2xl bg-slate-50 hover:bg-orange-50/20 cursor-pointer transition-all">
              <FileText className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-xs font-semibold text-slate-800">
                {file ? file.name : "Click or drag PDF here to upload"}
              </span>
              <span className="text-[11px] text-slate-400 mt-0.5">
                {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Supports any size & dimension (A4, Letter, Landscape)"}
              </span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>

          {/* Title & Slug */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Edition Title"
                required
                disabled={uploading}
                className="w-full px-3.5 py-2 text-xs rounded-xl bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                URL Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="edition-slug"
                required
                disabled={uploading}
                className="w-full px-3.5 py-2 text-xs rounded-xl bg-white border border-slate-200 text-slate-800 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief summary of this publication..."
              disabled={uploading}
              className="w-full px-3.5 py-2 text-xs rounded-xl bg-white border border-slate-200 text-slate-800 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>

          {/* Access Policy */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Access Mode
            </label>
            <select
              value={accessType}
              onChange={(e) => setAccessType(e.target.value)}
              disabled={uploading}
              className="w-full px-3.5 py-2 text-xs rounded-xl bg-white border border-slate-200 font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer"
            >
              <option value="FREE">Free (Public Reading)</option>
              <option value="MEMBER_ONLY">Member Exclusive</option>
              <option value="EMAIL_LEAD">Email Lead Capture</option>
              <option value="PASSCODE">Passcode Protected</option>
              <option value="PAID">Paid Edition</option>
            </select>
          </div>

          {/* Role selector if MEMBER_ONLY */}
          {accessType === "MEMBER_ONLY" && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <span className="text-[11px] font-bold text-slate-600 block">
                Allowed Roles:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {systemRoles.map((r) => {
                  const roleName = r.name || r.role_name;
                  const isSelected = allowedGroups.includes(roleName);
                  return (
                    <button
                      type="button"
                      key={r.id || roleName}
                      onClick={() => handleToggleRole(roleName)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all cursor-pointer ${
                        isSelected
                          ? "bg-orange-600 text-white border-orange-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      {roleName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          {uploading && (
            <div className="p-3.5 bg-orange-50/70 rounded-xl border border-orange-200 space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold text-orange-950">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-600" />
                  {statusMessage}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-orange-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold shadow-sm shadow-orange-600/20 disabled:opacity-50 transition-all cursor-pointer"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Converting PDF...</span>
                </>
              ) : (
                <>
                  <UploadCloud className="w-3.5 h-3.5" />
                  <span>Import &amp; Convert PDF</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
